import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Check,
  Clock,
  X,
  Download,
  FileText,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Receipt,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import {
  deleteSponsorBureau,
  fetchBureauSponsorFiche,
  refuserVisuelSponsor,
  validerVisuelSponsor,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import SessionPerdue from '../components/SessionPerdue';
import { euros } from '../utils/format';

const dateCourte = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatDate(iso: string | null | undefined): string {
  return iso ? dateCourte.format(new Date(iso)) : '—';
}

const statutDocument: Record<string, { label: string; classe: string }> = {
  en_attente: { label: 'En attente', classe: 'bg-amber-50 text-amber-700' },
  valide: { label: 'Validé', classe: 'bg-green-50 text-green-700' },
  refuse: { label: 'Refusé', classe: 'bg-red-50 text-red-700' },
  en_attente_paiement: { label: 'En attente de paiement', classe: 'bg-amber-50 text-amber-700' },
  payee: { label: 'Payée', classe: 'bg-green-50 text-green-700' },
  impayee: { label: 'Impayée', classe: 'bg-red-50 text-red-700' },
  emis: { label: 'Émis', classe: 'bg-purple-50 text-purple-700' },
};

const iconeDocument = {
  devis: FileText,
  facture: Receipt,
  avoir: RotateCcw,
} as const;

type Onglet = 'finances' | 'documents' | 'visuels';

/**
 * Fiche d'un sponsor : qui il est, ce qu'il a versé, ce qu'il doit, et tout
 * ce qui a été échangé avec lui — documents comptables comme visuels.
 */
export default function SponsorFichePage() {
  const { id } = useParams<{ id: string }>();
  const [onglet, setOnglet] = useState<Onglet>('finances');
  const [notice, setNotice] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { aLeDroit } = useAuth();

  // Le contrôle relève de la grille : c'est un arbitrage sur ce qui sera
  // publié au nom du comité, et la fonction qui tient les partenaires en
  // répond. L'exiger d'un administrateur posait une seconde serrure sur une
  // porte que la grille dit déjà qui peut ouvrir.
  const peutControler = aLeDroit('modifier_sponsors');

  const rafraichir = (message: string) => {
    setNotice(message);
    queryClient.invalidateQueries({ queryKey: ['bureau-sponsor', id] });
  };

  const [erreurSuppression, setErreurSuppression] = useState<string | null>(null);
  const supprimer = useMutation({
    mutationFn: () => deleteSponsorBureau(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bureau-sponsors'] });
      // La fiche n'existe plus : rester dessus afficherait une erreur.
      navigate('/sponsors', { replace: true });
    },
    onError: (e: Error) => setErreurSuppression(e.message),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['bureau-sponsor', id],
    queryFn: () => fetchBureauSponsorFiche(Number(id)),
    enabled: Boolean(id),
  });

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return <SessionPerdue message={(error as Error).message} />;
  }

  const sponsor = data.sponsor;
  const suivi = data.suiviFinancier;
  const documents: any[] = data.documents ?? [];
  const visuels: any[] = data.visuels ?? [];

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <Link
        to="/sponsors"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400"
      >
        <ArrowLeft className="w-4 h-4" /> Tous les sponsors
      </Link>

      {notice && (
        <p className="text-sm rounded-xl px-3 py-2 bg-green-50 text-green-700">{notice}</p>
      )}

      {/* Identité */}
      <header className="card lg:p-6 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-primary-700 dark:text-primary-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
              {sponsor.nom || 'Sans nom'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {[sponsor.activite, sponsor.formeJuridique].filter(Boolean).join(' · ') || 'Activité non renseignée'}
            </p>
          </div>

          {/* Suppression d'une fiche fictive : le tri qui suit le passage en
              production. En production, le serveur refuse dès qu'une facture
              existe — la numérotation légale doit rester continue. */}
          {peutControler && (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Supprimer définitivement la fiche « ${sponsor.nom} » et tout son dossier (souscriptions, devis, factures, visuels) ?`
                  )
                ) {
                  supprimer.mutate();
                }
              }}
              disabled={supprimer.isPending}
              title="Supprimer la fiche et son dossier"
              className="shrink-0 p-2 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
            >
              {supprimer.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5" />
              )}
            </button>
          )}
        </div>

        {erreurSuppression && (
          <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">{erreurSuppression}</p>
        )}

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Coordonnee icone={Mail} valeur={sponsor.email} href={sponsor.email ? `mailto:${sponsor.email}` : null} />
          <Coordonnee icone={Phone} valeur={sponsor.telephone} href={sponsor.telephone ? `tel:${sponsor.telephone}` : null} />
          <Coordonnee icone={MapPin} valeur={sponsor.adresse} />
          <Coordonnee
            icone={Globe}
            valeur={sponsor.siteWeb?.replace(/^https?:\/\//, '')}
            href={sponsor.siteWeb}
          />
        </dl>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-800 dark:text-gray-400">
          {sponsor.contactPrenom || sponsor.contactNom ? (
            <span>Contact : {[sponsor.contactPrenom, sponsor.contactNom].filter(Boolean).join(' ')}</span>
          ) : null}
          {sponsor.siret && <span>SIRET {sponsor.siret}</span>}
          {sponsor.tvaIntracommunautaire && <span>TVA {sponsor.tvaIntracommunautaire}</span>}
          {sponsor.dateImmatriculation && (
            <span>Immatriculé le {formatDate(sponsor.dateImmatriculation)}</span>
          )}
        </div>

        {sponsor.message && (
          <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-xl p-3 whitespace-pre-line">
            {sponsor.message}
          </p>
        )}
      </header>

      {/* Suivi financier — chiffres clés */}
      <div className="grid grid-cols-3 gap-3">
        <Indicateur libelle="Souscrit" valeur={suivi.totalSouscrit} />
        <Indicateur libelle="Encaissé" valeur={suivi.totalEncaisse} accent="text-green-600" />
        <Indicateur
          libelle="Reste dû"
          valeur={suivi.resteDu}
          accent={suivi.resteDu > 0 ? 'text-amber-600' : 'text-gray-400'}
        />
      </div>

      {/* Onglets */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
        <Onglet actif={onglet === 'finances'} onClick={() => setOnglet('finances')}>
          Suivi financier
        </Onglet>
        <Onglet actif={onglet === 'documents'} onClick={() => setOnglet('documents')}>
          Documents ({documents.length})
        </Onglet>
        <Onglet actif={onglet === 'visuels'} onClick={() => setOnglet('visuels')}>
          Visuels ({visuels.length})
        </Onglet>
      </div>

      {onglet === 'finances' && (
        <section className="card p-0 overflow-x-auto">
          {suivi.parAnnee.length === 0 ? (
            <p className="p-6 text-center text-gray-500 dark:text-gray-400">Aucun mouvement enregistré.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Exercice</th>
                  <th className="px-4 py-3 font-medium text-right">Souscrit</th>
                  <th className="px-4 py-3 font-medium text-right">Encaissé</th>
                  <th className="px-4 py-3 font-medium text-right">Reste</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {suivi.parAnnee.map((ligne: any) => (
                  <tr key={ligne.annee}>
                    <td className="px-4 py-3 font-medium">{ligne.annee}</td>
                    <td className="px-4 py-3 text-right">{euros.format(ligne.souscrit)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{euros.format(ligne.encaisse)}</td>
                    <td className={`px-4 py-3 text-right ${ligne.reste > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
                      {ligne.reste > 0 ? euros.format(ligne.reste) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {onglet === 'documents' && (
        <section className="space-y-2">
          {documents.length === 0 ? (
            <p className="card text-center text-gray-500 py-8 dark:text-gray-400">Aucun document comptable.</p>
          ) : (
            documents.map((doc) => {
              const Icone = iconeDocument[doc.categorie as keyof typeof iconeDocument] ?? FileText;
              const statut = statutDocument[doc.statut] ?? { label: doc.statut, classe: 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700' };

              return (
                <article
                  key={`${doc.categorie}-${doc.id}`}
                  className="card flex items-center gap-3 py-3"
                >
                  <Icone className={`w-5 h-5 shrink-0 ${doc.categorie === 'avoir' ? 'text-red-500' : 'text-primary-600'}`} />

                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">
                      {doc.libelle} {doc.numero}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(doc.date)}
                      {doc.payeeLe && ` · payée le ${formatDate(doc.payeeLe)}`}
                      {!doc.payeeLe && doc.echeanceLe && ` · échéance ${formatDate(doc.echeanceLe)}`}
                    </p>
                  </div>

                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${statut.classe}`}>
                    {statut.label}
                  </span>

                  <span className={`text-sm font-semibold shrink-0 tabular-nums ${doc.montant < 0 ? 'text-red-600' : ''}`}>
                    {euros.format(doc.montant)}
                  </span>

                  <a
                    href={doc.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    title="Télécharger le PDF"
                    className="text-gray-400 hover:text-primary-600 shrink-0"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </article>
              );
            })
          )}
        </section>
      )}

      {onglet === 'visuels' && (
        <section>
          {visuels.length === 0 ? (
            <p className="card text-center text-gray-500 py-8 dark:text-gray-400">Aucun visuel déposé.</p>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {visuels.map((f) => {
                const estImage = String(f.mimeType ?? '').startsWith('image/');

                return (
                  <li key={f.id} className="card p-0 overflow-hidden">
                    <a href={f.url} target="_blank" rel="noreferrer" className="block">
                      <div className="aspect-video bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                        {estImage ? (
                          <img src={f.url} alt={f.nomOriginal ?? ''} className="w-full h-full object-contain" />
                        ) : (
                          <FileText className="w-8 h-8 text-gray-300" />
                        )}
                      </div>
                      <div className="p-2 space-y-0.5">
                        <p className="text-xs font-medium truncate" title={f.nomOriginal ?? ''}>
                          {f.nomOriginal || 'Fichier'}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {f.libelle} · {formatDate(f.uploadedAt)}
                        </p>
                      </div>
                    </a>

                    {/* Le contrôle ne porte que sur ce qui sera publié : un
                        devis en PDF n'a pas à être jugé conforme. */}
                    {f.soumisAControle && (
                      <ControleVisuel fichier={f} peutControler={peutControler} onFait={rafraichir} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * Contrôle de conformité d'un visuel déposé.
 *
 * Le sponsor est prévenu dans les deux cas : un visuel déposé sans réponse le
 * laisse dans le doute jusqu'à la parution du livret — trop tard pour
 * corriger. Le refus porte son motif, communiqué tel quel, pour qu'il puisse
 * redéposer sans téléphoner.
 */
function ControleVisuel({
  fichier,
  peutControler,
  onFait,
}: {
  fichier: any;
  peutControler: boolean;
  onFait: (message: string) => void;
}) {
  const [refusOuvert, setRefusOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const valider = useMutation({
    mutationFn: () => validerVisuelSponsor(fichier.id),
    onSuccess: (d) => onFait(d.message),
    onError: (e: Error) => setErreur(e.message),
  });

  const refuser = useMutation({
    mutationFn: () => refuserVisuelSponsor(fichier.id, motif.trim()),
    onSuccess: (d) => {
      onFait(d.message);
      setRefusOuvert(false);
      setMotif('');
    },
    onError: (e: Error) => setErreur(e.message),
  });

  const etats: Record<string, { libelle: string; classe: string; Icone: typeof Check }> = {
    en_attente: { libelle: 'À contrôler', classe: 'bg-amber-50 text-amber-700', Icone: Clock },
    valide: { libelle: 'Conforme', classe: 'bg-green-50 text-green-700', Icone: Check },
    refuse: { libelle: 'Non conforme', classe: 'bg-red-50 text-red-700', Icone: X },
  };

  const etat = etats[fichier.statutValidation] ?? etats.en_attente;
  const Icone = etat.Icone;
  const enCours = valider.isPending || refuser.isPending;

  return (
    <div className="px-2 pb-2 space-y-1.5 border-t border-gray-100 dark:border-gray-800 pt-2">
      <span
        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${etat.classe}`}
        title={fichier.controlePar ? `Contrôlé par ${fichier.controlePar}` : undefined}
      >
        <Icone className="w-3 h-3" />
        {etat.libelle}
      </span>

      {fichier.motifRefus && (
        <p className="text-[11px] text-red-700 bg-red-50 rounded p-1.5 whitespace-pre-line">
          {fichier.motifRefus}
        </p>
      )}

      {erreur && <p className="text-[11px] text-red-600">{erreur}</p>}

      {peutControler && !refusOuvert && (
        <div className="flex flex-wrap gap-1.5">
          {fichier.statutValidation !== 'valide' && (
            <button
              type="button"
              onClick={() => {
                setErreur(null);
                valider.mutate();
              }}
              disabled={enCours}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-green-600 text-white disabled:opacity-50"
            >
              {valider.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Conforme
            </button>
          )}
          {fichier.statutValidation !== 'refuse' && (
            <button
              type="button"
              onClick={() => {
                setErreur(null);
                setRefusOuvert(true);
              }}
              disabled={enCours}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-red-300 text-red-700 disabled:opacity-50"
            >
              <X className="w-3 h-3" /> Refuser
            </button>
          )}
        </div>
      )}

      {peutControler && refusOuvert && (
        <div className="space-y-1.5">
          <textarea
            className="input text-[11px] min-h-[4rem] py-1"
            placeholder="Motif — il est envoyé tel quel au sponsor (résolution trop faible, format non vectoriel…)"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => refuser.mutate()}
              disabled={refuser.isPending || motif.trim() === ''}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-red-600 text-white disabled:opacity-50"
            >
              {refuser.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
              Envoyer le refus
            </button>
            <button
              type="button"
              onClick={() => setRefusOuvert(false)}
              className="text-[11px] text-gray-500 dark:text-gray-400"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Indicateur({
  libelle,
  valeur,
  accent = 'text-gray-900 dark:text-gray-100',
}: {
  libelle: string;
  valeur: number;
  accent?: string;
}) {
  return (
    <div className="card py-3 text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400">{libelle}</p>
      <p className={`text-lg lg:text-xl font-bold tabular-nums ${accent}`}>{euros.format(valeur)}</p>
    </div>
  );
}

function Coordonnee({
  icone: Icone,
  valeur,
  href,
}: {
  icone: typeof Mail;
  valeur?: string | null;
  href?: string | null;
}) {
  if (!valeur) return null;

  return (
    <div className="flex items-start gap-2 text-gray-600 dark:text-gray-300 min-w-0">
      <Icone className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
      {href ? (
        <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="hover:text-primary-600 truncate">
          {valeur}
        </a>
      ) : (
        <span className="truncate">{valeur}</span>
      )}
    </div>
  );
}

function Onglet({
  actif,
  onClick,
  children,
}: {
  actif: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
        actif
          ? 'border-primary-600 text-primary-700 dark:text-primary-400'
          : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400'
      }`}
    >
      {children}
    </button>
  );
}
